/*============================================================
   REACT DEPENDENCIES
============================================================*/
import { useState, useEffect } from "react";

/*============================================================
   REDUX DEPENDENCIES
============================================================*/
import { useDispatch, useSelector } from "react-redux";

import Grid from "@mui/material/Grid2";
import FormHelperText from "@mui/material/FormHelperText";
import TextareaAutosize from "@mui/material/TextareaAutosize";
import Typography from "@mui/material/Typography";

import GridSectionHeader from "../../widgets/GridSectionHeader";
import GridTextfield from "../../widgets/GridTextfield";
import GridDropdown from "../../widgets/GridDropdown";
import GridCheckbox from "../../widgets/GridCheckbox";

import { valudateTextfield2 } from "../../../utils/fieldValidator.js";

import { updateHospitalization } from "../../../states/slcObjectives.js";

const governmentStructured = [
  { key: "A", value: "A" },
  { key: "B1", value: "B1" },
  { key: "B2", value: "B2/C" },
];

const deductiblesAndCoInsurance = [
  { key: "NA", value: "Not Applicable" },
  { key: "DEDUCTIBLE", value: "Deductible" },
  { key: "COINSURANCDE", value: "Co-Insurance" },
  { key: "DEDUCT_COINSR", value: "Deductible & Co-Insurance" },
];

export default function Hospitalisation() {
  const hospitalization = useSelector(
    (state) => state.objectives.protection.hospitalization
  );

  const [hsHospitalType, setHsHospitalType] = useState(
    hospitalization.hsHospitalType
  );

  const [hsGovRestructured, setHsGovRestructured] = useState(
    hospitalization.hsGovRestructured
  );
  const [hsProvDeductCoInsurance, setHsProvDeductCoInsurance] = useState(
    hospitalization.hsProvDeductCoInsurance
  );
  const [hsCurrHospitalCover, setHsCurrHospitalCover] = useState(
    hospitalization.hsCurrHospitalCover
  );
  const [hsInsurerNameAndType, setHsInsurerNameAndType] = useState(
    hospitalization.hsInsurerNameAndType
  );

  const [deviationReason, setDeviationReason] = useState(
    hospitalization.deviationReason
  );

  //ERRORS
  const [hsHospitalTypeError, setHsHospitalTypeError] = useState();
  const [hsGovRestructuredError, setHsGovRestructuredError] = useState();
  const [hsProvDeductCoInsuranceError, setHsProvDeductCoInsuranceError] =
    useState();
  const [hsCurrHospitalCoverError, setHsCurrHospitalCoverError] = useState();
  const [hsInsurerNameAndTypeError, setHsInsurerNameAndTypeError] = useState();
  const [deviationReasonError, setDeviationReasonError] = useState();

  const dispatch = useDispatch();

  //COMPONENT ID
  let id = "";

  function updateRedux(fieldName, fieldValue, reducer) {
    const action = {
      type: fieldName,
      payload: fieldValue,
    };
    dispatch(reducer(action));
  }

  function handleChange(e, object) {
    let input_data = {
      event: e,
      fieldName: "",
      fieldVariable: "",
      isMandatory: false,
    };

    if (e instanceof PointerEvent) id = object.props.id; ////DROPDOWNS
    else id = e.target.id; ////TEXTFIELDS

    validateFields(e, id, input_data);
  }

  function validateFields(e, id, input_data) {
    if (id === "hsHospitalType") {
      setHsHospitalType(e.target.checked);
      updateRedux("hsHospitalType", e.target.checked, updateHospitalization);
      setHsHospitalTypeError("");
    }

    if (id === "hsGovRestructured") {
      setHsGovRestructured(e.target.value);
      updateRedux("hsGovRestructured", e.target.value, updateHospitalization);
      setHsProvDeductCoInsuranceError("");
    }

    if (id === "hsProvDeductCoInsurance") {
      setHsProvDeductCoInsurance(e.target.value);
      updateRedux(
        "hsProvDeductCoInsurance",
        e.target.value,
        updateHospitalization
      );
      setHsGovRestructuredError("");
    }

    if (id === "hsCurrHospitalCover") {
      input_data.fieldName = "Current hospital cover type";
      input_data.fieldVariable = hsCurrHospitalCover;
      const result = valudateTextfield2(input_data);
      if (result.errorMessage === "") {
        setHsCurrHospitalCover(result.value);
        updateRedux("hsCurrHospitalCover", result.value, updateHospitalization);
      }

      setHsCurrHospitalCoverError(result.errorMessage);
    }

    if (id === "hsInsurerNameAndType") {
      input_data.fieldName = "Insurer namw and type";
      input_data.fieldVariable = hsInsurerNameAndType;
      const result = valudateTextfield2(input_data);
      if (result.errorMessage === "") {
        setHsInsurerNameAndType(result.value);
        updateRedux(
          "hsInsurerNameAndType",
          result.value,
          updateHospitalization
        );
      }

      setHsCurrHospitalCoverError(result.errorMessage);
    }

    if (id === "deviationReason") {
      input_data.fieldName = "Deviation reason";
      input_data.fieldLength = 1000;
      input_data.fieldVariable = deviationReason;
      const result = valudateTextfield2(input_data);
      if (result.errorMessage === "") {
        setDeviationReason(result.value);
        updateRedux("deviationReason", result.value, updateHospitalization);
      }

      setDeviationReasonError(result.errorMessage);
    }
  }

  return (
    <>
      <Grid container spacing={1} size={12}>
        <GridSectionHeader
          sectionName="In the Event of Hospitalisation / Medical Expenses"
          sx={{
            p: 1,
            pl: 1.8,
            mt: 0,
            mb: 1,
            fontSize: 18,
            fontWeight: "bold",
            color: "#FFFFFF", //WHITE
            // color: "#000000", //BLACK
            // backgroundColor: "#d9d9d9",
            backgroundColor: "#10B981",
          }}
        />

        <Grid size={9}>
          <Typography
            variant="body1"
            sx={{
              pt: 1.2,
            }}
          >
            Preferred Hospital Type and Ward?{" "}
          </Typography>
        </Grid>
        <GridCheckbox
          id="hsHospitalType"
          columnSize="3"
          label="Private"
          required
          currState={hsHospitalType}
          onChange={handleChange}
        />

        <Grid size={9}>
          <Typography
            variant="body1"
            sx={{
              pt: 2.0,
            }}
          >
            Government Restructured
          </Typography>
        </Grid>
        <GridDropdown
          id="hsGovRestructured"
          key="hsGovRestructured"
          columnSize="3"
          currState={hsGovRestructured}
          onChange={handleChange}
          data={governmentStructured}
          error={hsGovRestructuredError}
        />

        {/*==============================
        PROVISION FOR DEDUCTIBLES
        ==============================*/}
        <Grid size={9}>
          <Typography
            variant="body1"
            sx={{
              pt: 2.0,
            }}
          >
            Provision for Deductible and/or Co-Insurance?
          </Typography>
        </Grid>
        <GridDropdown
          id="hsProvDeductCoInsurance"
          key="hsProvDeductCoInsurance"
          columnSize="3"
          currState={hsProvDeductCoInsurance}
          onChange={handleChange}
          data={governmentStructured}
          error={hsProvDeductCoInsuranceError}
        />

        {/*==============================
        EXISTING HOSPITAL PLAN COVER
        ==============================*/}
        <Grid size={9}>
          <Typography
            variant="body1"
            sx={{
              pt: 2.0,
            }}
          >
            Existing type of hospital plan cover?
          </Typography>
        </Grid>

        <GridTextfield
          id="hsCurrHospitalCover"
          key="hsCurrHospitalCover"
          columnSize="3"
          currState={hsCurrHospitalCover}
          onChange={handleChange}
          error={hsCurrHospitalCoverError}
          helperText={hsCurrHospitalCoverError}
        />

        {/*==============================
        EXISTING INSURER NAME AND PLAN TYPE
        ==============================*/}
        <Grid size={9}>
          <Typography
            variant="body1"
            sx={{
              pt: 2.0,
            }}
          >
            Insurer name and plan type?
          </Typography>
        </Grid>

        <GridTextfield
          id="hsInsurerNameAndType"
          key="hsInsurerNameAndType"
          columnSize="3"
          currState={hsInsurerNameAndType}
          onChange={handleChange}
          error={hsInsurerNameAndTypeError}
          helperText={hsInsurerNameAndTypeError}
        />

        {/*==============================
        REASON(S) FOR DEVIATIONS
        ==============================*/}
        <Typography variant="body1">Reason(s) for Deviations:</Typography>
        <TextareaAutosize
          id="deviationReason"
          placeholder="Type here..."
          style={{ width: "100%" }}
          minRows={5}
          onChange={handleChange}
          defaultValue={deviationReason}
        />
        {deviationReasonError && (
          <FormHelperText error={deviationReasonError}>
            {deviationReasonError}
          </FormHelperText>
        )}
      </Grid>
    </>
  );
}
